import { useCallback } from "react";
import type { CoverageGap, UncoveredClaim } from "@shared/types/corpus/api.ts";
import {
  attachClaimToCoverageGap,
  createCoverageGap,
  getCoverageGaps,
  getUngroupedUncoveredClaims,
} from "../../../../lib/corpusApi";
import { useAsyncData } from "../../../../hooks/useAsyncData";
import { useAsyncPaginatedData } from "../../../../hooks/useAsyncPaginatedData";
import { useSelection, type Selection } from "../../../../hooks/useSelection";
import { PAGE_SIZE_OPTIONS, type ListPagination } from "../../../../lib/pagination";
import { useUrlFilters } from "../../../../lib/url_filters";
import { uncoveredClaimFiltersSchema, type UncoveredClaimFilters } from "./uncoveredClaimFilters";

const OPEN_GAPS_LIMIT = 100;

export interface UseUncoveredClaimsResult {
  filters: UncoveredClaimFilters;
  setFilters: (filters: UncoveredClaimFilters) => void;
  /** The uncovered claims in no gap yet, ranked by `filters.sort`. */
  claims: {
    items: UncoveredClaim[];
    loading: boolean;
    error: string | null;
    pagination: ListPagination;
  };
  /** The open gaps, which a claim can be put in. */
  openGaps: CoverageGap[];
  /** The claims ticked for adding to a gap. */
  selection: Selection;
  createGap: (name: string, note: string) => Promise<CoverageGap>;
  /** Puts claims in a gap. */
  attachClaims: (gapId: string, claimIds: string[]) => Promise<void>;
}

/** The uncovered claims not yet in a gap, with the grouping the operator does by hand. */
export function useUncoveredClaims(): UseUncoveredClaimsResult {
  const [filters, setFilters] = useUrlFilters(uncoveredClaimFiltersSchema);
  const selection = useSelection();
  const claimsData = useAsyncPaginatedData(
    (page) => getUngroupedUncoveredClaims(filters.sort, page),
    { pageSize: 25, key: filters.sort }
  );
  const openGapsData = useAsyncData(() =>
    getCoverageGaps({ state: "open", limit: OPEN_GAPS_LIMIT })
  );

  const { refresh: refreshClaims } = claimsData;
  const { refresh: refreshGaps } = openGapsData;
  const { unselectAll } = selection;

  const refreshAll = useCallback(async () => {
    unselectAll();
    await Promise.all([refreshClaims(), refreshGaps()]);
  }, [refreshClaims, refreshGaps, unselectAll]);

  const createGap = useCallback(
    async (name: string, note: string) => {
      const gap = await createCoverageGap({ name, note });
      await refreshGaps();
      return gap;
    },
    [refreshGaps]
  );

  const attachClaims = useCallback(
    async (gapId: string, claimIds: string[]) => {
      try {
        // One claim at a time: each is its own attach, so a failure leaves the earlier ones in.
        for (const claimId of claimIds) await attachClaimToCoverageGap(gapId, claimId);
      } finally {
        await refreshAll();
      }
    },
    [refreshAll]
  );

  return {
    filters,
    setFilters,
    claims: {
      items: claimsData.items,
      loading: claimsData.loading,
      error: claimsData.error,
      pagination: { ...claimsData.pagination, pageSizeOptions: PAGE_SIZE_OPTIONS },
    },
    openGaps: openGapsData.data?.items ?? [],
    selection,
    createGap,
    attachClaims,
  };
}
